import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { FileText, ChevronLeft, ChevronRight, Printer, ChevronDown, X, AlertTriangle, Check } from "lucide-react";

const DEPARTMENTS = [
  { key: "dough_section", label: "Dough Section" },
  { key: "top_room", label: "Top Room" },
  { key: "angels_section", label: "Angels Section" },
];

const DEPARTMENT_DOUGHS = {
  top_room: [
    "Egg bun dough", "Milk bun dough", "Pizza dough", "Fruity dough", "Lavash", "Grissini",
    "Bread roll dough", "Potato bun dough", "Red burger dough", "Black burger dough", "Ciabatta",
    "French baguette", "Croissants", "Puffs dough", "Cookies/biscotti", "Cakes/Brownies",
    "Indian Snacks", "Burger Dough",
  ],
  dough_section: [
    "Milk dough", "Brioche Dough", "Burger Dough", "White Dough", "Brown Dough", "Pav Dough",
    "Sweet dough", "Butter bread dough", "Butter brioche dough", "Multigrain dough",
  ],
  angels_section: ["Crumbs dough"],
};

const TOP_ROOM_BURGER_ITEMS = [
  "hot hotdog bun*4", "lul samoon seeded*4", "hotdog buns- 8in", "lul hotdog bun 3s",
  "sandwich buns- 6in*4", "pvr sandwich ( hotdog) bun 6in*4", "pvr hotdog 6.5in*4",
];

const DEPT_STAFF_KEY = {
  top_room: "top_room",
  dough_section: "dough_section",
  angels_section: "angels_prep",
};

function todayIST() {
  // IST = UTC + 5:30, independent of the browser's local timezone
  return new Date(Date.now() + 5.5 * 60 * 60000).toISOString().slice(0, 10);
}

function calcAdjustedValues(item) {
  const stock = item.previous_closing_stock || 0;
  const todayPrep = Math.max(0, item.orders_today - stock);
  const remainingAfterToday = stock - item.orders_today;
  const tomorrowPrep = Math.max(0, item.orders_tomorrow - Math.max(0, remainingAfterToday));
  return { todayPrep, tomorrowPrep };
}

export default function PrepReport() {
  const [date, setDate] = useState(todayIST());
  const [department, setDepartment] = useState("dough_section");
  const [reportedBy, setReportedBy] = useState("");
  const [staff, setStaff] = useState([]);
  const [items, setItems] = useState([]);
  const [prepared, setPrepared] = useState({});
  const [loading, setLoading] = useState(false);
  const [deptOpen, setDeptOpen] = useState(false);
  const [staffOpen, setStaffOpen] = useState(false);
  const [choiceModal, setChoiceModal] = useState(false);
  const [excessItems, setExcessItems] = useState([]);
  const [excessModal, setExcessModal] = useState(false);

  const prepKey = (d, dep) => `dc_prepared_${d}_${dep}`;

  // Load report items for the selected department + date
  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/reports/preparation-list?date=${date}`);
      const deptDoughs = DEPARTMENT_DOUGHS[department] || [];
      const filtered = (r.data.items || []).filter((item) => {
        const doughName = (item.dough_type_name || "").toLowerCase();
        const productName = (item.product_name || "").toLowerCase();
        const stock = item.previous_closing_stock || 0;
        const adjToday = Math.max(0, item.orders_today - stock);
        const remainAfter = stock - item.orders_today;
        const adjTmrw = Math.max(0, item.orders_tomorrow - Math.max(0, remainAfter));
        if (!(adjToday > 0 || adjTmrw > 0)) return false;

        const isBurgerDough = doughName === "burger dough";
        const isTopRoomBurgerItem = TOP_ROOM_BURGER_ITEMS.some((n) => productName === n);

        if (department === "top_room") {
          if (isBurgerDough) return isTopRoomBurgerItem;
          return deptDoughs.some((d) => d.toLowerCase() === doughName);
        } else if (department === "dough_section") {
          if (isBurgerDough && isTopRoomBurgerItem) return false;
          return deptDoughs.some((d) => d.toLowerCase() === doughName);
        }
        return deptDoughs.some((d) => d.toLowerCase() === doughName);
      });
      setItems(filtered);
      // restore saved prepared quantities
      try {
        const saved = localStorage.getItem(prepKey(date, department));
        setPrepared(saved ? JSON.parse(saved) : {});
      } catch { setPrepared({}); }
    } catch {
      toast.error("Failed to load prep report");
    } finally {
      setLoading(false);
    }
  }, [date, department]);

  const loadStaff = useCallback(async () => {
    try {
      const key = DEPT_STAFF_KEY[department] || department;
      const r = await api.get(`/admin/section-staff/${key}`);
      setStaff((r.data.staff || []).filter((s) => s.is_active));
      setReportedBy("");
    } catch { setStaff([]); }
  }, [department]);

  const calcExcess = useCallback(async () => {
    try {
      const [prods, prep] = await Promise.all([
        api.get("/products"),
        api.get(`/admin/reports/preparation-list?date=${date}`),
      ]);
      const reqMap = {};
      (prep.data.items || []).forEach((it) => { reqMap[it.product_name] = it.orders_today || 0; });
      const savedPrep = (() => { try { return JSON.parse(localStorage.getItem(prepKey(date, department)) || "{}"); } catch { return {}; } })();
      const excess = (prods.data || [])
        .map((p) => {
          const stock = p.previous_closing_stock ?? p.closing_stock ?? 0;
          const todayReq = reqMap[p.name] || 0;
          const prep = parseFloat(savedPrep[p.name] || "0") || 0;
          return {
            name: p.name, stock, todayOrders: todayReq,
            needToday: todayReq - stock,
            excess: stock + prep - todayReq,
          };
        })
        .filter((x) => x.excess > 0)
        .sort((a, b) => b.excess - a.excess);
      setExcessItems(excess);
      if (excess.length > 0) setExcessModal(true);
    } catch { /* ignore */ }
  }, [date, department]);

  useEffect(() => { loadItems(); loadStaff(); }, [loadItems, loadStaff]);
  // Auto-show excess stock alert when items load
  useEffect(() => { if (!loading) { const t = setTimeout(() => calcExcess(), 400); return () => clearTimeout(t); } }, [loading, calcExcess]);

  const setPrep = (key, val) => {
    setPrepared((prev) => {
      const next = { ...prev, [key]: val };
      try { localStorage.setItem(prepKey(date, department), JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const changeDate = (days) => {
    const dt = new Date(date + "T12:00:00"); dt.setDate(dt.getDate() + days);
    setDate(dt.toISOString().slice(0, 10));
  };

  // Group items by dough
  const grouped = {};
  items.forEach((item) => {
    const dough = item.dough_type_name || "Other";
    if (!grouped[dough]) grouped[dough] = [];
    grouped[dough].push(item);
  });

  const handlePrint = (limited) => {
    if (items.length === 0) return toast.error("No items to print");
    const deptLabel = DEPARTMENTS.find((d) => d.key === department)?.label || "";
    let rows = "";
    Object.entries(grouped).forEach(([dough, its]) => {
      its.forEach((item, idx) => {
        const { todayPrep, tomorrowPrep } = calcAdjustedValues(item);
        const prep = parseFloat(prepared[item.product_name] || "0") || 0;
        const notDone = Math.max(0, todayPrep + tomorrowPrep - prep);
        rows += `<tr>
          <td style="font-weight:${idx === 0 ? "bold" : "normal"}">${idx === 0 ? dough : ""}</td>
          <td>${item.product_name}</td>
          <td style="text-align:center">${todayPrep}</td>
          <td style="text-align:center">${tomorrowPrep}</td>
          ${limited ? "" : `<td style="text-align:center">${prep || ""}</td>
          <td style="text-align:center;font-weight:bold;color:${notDone > 0 ? "#c00" : "#333"}">${notDone > 0 ? notDone : "-"}</td>`}
        </tr>`;
      });
    });
    const thCols = limited
      ? `<th>Dough</th><th>Items</th><th style="text-align:center">Today</th><th style="text-align:center">Tmrw</th>`
      : `<th>Dough</th><th>Items</th><th style="text-align:center">Today</th><th style="text-align:center">Tmrw</th><th style="text-align:center">Prepared</th><th style="text-align:center">Not Done</th>`;
    const dateStr = new Date(date + "T12:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>
      @page { size: A4; margin: 8mm; } * { box-sizing: border-box; }
      body { font-family: Arial, sans-serif; font-size: 20px; color: #333; margin: 0; padding: 0; }
      .header { font-size: 22px; margin: 0 0 6px; } .header b { font-size: 24px; }
      table { width: 100%; border-collapse: collapse; margin-top: 4px; }
      th { background: #333; color: #fff; padding: 5px 6px; font-size: 18px; text-align: left; }
      td { padding: 4px 6px; border-bottom: 1px solid #ccc; font-size: 18px; line-height: 1.3; }
      tr { page-break-inside: avoid; } thead { display: table-header-group; }
    </style></head><body>
      <div class="header"><b>Preparation Report</b> | ${deptLabel} | ${reportedBy || "-"} | ${dateStr}</div>
      <table><thead><tr>${thCols}</tr></thead><tbody>${rows}</tbody></table>
    </body></html>`;
    const iframe = document.createElement("iframe");
    iframe.style.cssText = "position:fixed;left:0;top:0;width:100%;height:100%;border:none;opacity:0;pointer-events:none;";
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument;
    doc.open(); doc.write(html); doc.close();
    setTimeout(() => { iframe.contentWindow.focus(); iframe.contentWindow.print(); setTimeout(() => document.body.removeChild(iframe), 2000); }, 500);
  };

  const deptLabel = DEPARTMENTS.find((d) => d.key === department)?.label || "Select";

  return (
    <div className="animate-fade-up">
      <div className="flex items-center gap-2 mb-3">
        <FileText className="w-6 h-6 text-[#8B4513]" />
        <h1 className="text-xl font-bold text-[#8B4513] flex-1">Prep Report</h1>
        {excessItems.length > 0 && (
          <button onClick={() => setExcessModal(true)} data-testid="excess-indicator"
            className="flex items-center gap-1 bg-[#e65100] text-white rounded-full px-3 py-1.5">
            <AlertTriangle className="w-4 h-4" /><span className="font-bold text-sm">{excessItems.length}</span>
          </button>
        )}
      </div>

      {/* Date nav */}
      <div className="flex items-center justify-center gap-3 mb-3">
        <button onClick={() => changeDate(-1)} data-testid="prep-date-prev" className="p-2 text-[#8B4513]"><ChevronLeft className="w-6 h-6" /></button>
        <span className="font-semibold text-[#333]">{new Date(date + "T12:00:00").toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short", year: "numeric" })}</span>
        <button onClick={() => changeDate(1)} data-testid="prep-date-next" className="p-2 text-[#8B4513]"><ChevronRight className="w-6 h-6" /></button>
      </div>

      {/* Department dropdown */}
      <div className="mb-2">
        <label className="text-sm font-bold text-[#555]">Department:</label>
        <button onClick={() => setDeptOpen(true)} data-testid="dept-dropdown"
          className="w-full mt-1 flex items-center justify-between px-3 py-2.5 rounded-lg bg-white border border-[#e6dcc0] text-sm text-[#333]">
          {deptLabel}<ChevronDown className="w-4 h-4 text-[#666]" />
        </button>
      </div>

      {/* Reported by dropdown */}
      <div className="mb-3">
        <label className="text-sm font-bold text-[#555]">Reported by:</label>
        <button onClick={() => setStaffOpen(true)} data-testid="reportedby-dropdown"
          className="w-full mt-1 flex items-center justify-between px-3 py-2.5 rounded-lg bg-white border border-[#e6dcc0] text-sm">
          <span className={reportedBy ? "text-[#333]" : "text-[#999]"}>{reportedBy || "Select staff"}</span>
          <ChevronDown className="w-4 h-4 text-[#666]" />
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="space-y-2">{[...Array(6)].map((_, i) => <div key={i} className="h-12 rounded-lg skeleton" />)}</div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-xl py-12 text-center text-[#999]">
          <FileText className="w-10 h-10 mx-auto mb-2 opacity-40" />
          No items for this department.<br /><span className="text-xs">No preparation needed today/tomorrow.</span>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto border border-[#ddd] rounded-lg" data-testid="prep-table">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="bg-[#333] text-white text-left">
                  <th className="p-2 font-bold">Dough</th>
                  <th className="p-2 font-bold">Items</th>
                  <th className="p-2 font-bold text-center">Today</th>
                  <th className="p-2 font-bold text-center">Tmrw</th>
                  <th className="p-2 font-bold text-center">Prepared</th>
                  <th className="p-2 font-bold text-center">Not Done</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(grouped).map(([dough, its]) =>
                  its.map((item, idx) => {
                    const { todayPrep, tomorrowPrep } = calcAdjustedValues(item);
                    const prep = parseFloat(prepared[item.product_name] || "0") || 0;
                    const notDone = Math.max(0, todayPrep + tomorrowPrep - prep);
                    return (
                      <tr key={item.product_id} data-testid={`prep-row-${item.product_id}`} className={idx % 2 ? "bg-[#faf8f5]" : "bg-white"}>
                        <td className="p-2 font-bold text-[#5A3D1F] whitespace-nowrap">{idx === 0 ? dough : ""}</td>
                        <td className="p-2 text-[#333]">{item.product_name}</td>
                        <td className="p-2 text-center">{todayPrep}</td>
                        <td className="p-2 text-center">{tomorrowPrep}</td>
                        <td className="p-2 text-center">
                          <input type="number" value={prepared[item.product_name] || ""} placeholder="0"
                            data-testid={`prep-input-${item.product_id}`}
                            onChange={(e) => setPrep(item.product_name, e.target.value)}
                            className="w-14 px-1 py-1 rounded border border-[#e6dcc0] text-center bg-[#FFF8DC] focus:outline-none focus:border-[#8B4513]" />
                        </td>
                        <td className={`p-2 text-center font-bold ${notDone > 0 ? "text-[#c00]" : "text-[#333]"}`}>{notDone > 0 ? notDone : "-"}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <button onClick={() => setChoiceModal(true)} data-testid="prep-print-btn"
            className="w-full mt-4 flex items-center justify-center gap-2 py-3 rounded-xl bg-[#8B4513] hover:bg-[#703710] text-white font-bold">
            <Printer className="w-5 h-5" /> Print / Save PDF
          </button>
        </>
      )}

      {/* Department dropdown modal */}
      {deptOpen && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setDeptOpen(false)}>
          <div className="bg-white rounded-2xl w-full max-w-xs p-4" onClick={(e) => e.stopPropagation()} data-testid="dept-modal">
            <div className="font-bold text-[#8B4513] mb-2">Select Department</div>
            {DEPARTMENTS.map((d) => (
              <button key={d.key} data-testid={`dept-opt-${d.key}`}
                onClick={() => { setDepartment(d.key); setDeptOpen(false); }}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg mb-1 text-sm ${department === d.key ? "bg-[#FFF8DC] font-bold text-[#8B4513]" : "text-[#333]"}`}>
                {d.label}{department === d.key && <Check className="w-4 h-4 text-[#8B4513]" />}
              </button>
            ))}
          </div>
        </div>
      , document.body)}

      {/* Reported by dropdown modal */}
      {staffOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setStaffOpen(false)}>
          <div className="bg-white rounded-2xl w-full max-w-xs p-4" onClick={(e) => e.stopPropagation()} data-testid="staff-modal">
            <div className="font-bold text-[#8B4513] mb-2">Select Staff</div>
            {staff.length === 0 ? (
              <p className="text-sm text-[#999] py-3 text-center">No staff added for this department</p>
            ) : staff.map((s) => (
              <button key={s.id} data-testid={`staff-opt-${s.id}`}
                onClick={() => { setReportedBy(s.name); setStaffOpen(false); }}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg mb-1 text-sm ${reportedBy === s.name ? "bg-[#FFF8DC] font-bold text-[#8B4513]" : "text-[#333]"}`}>
                {s.name}{reportedBy === s.name && <Check className="w-4 h-4 text-[#8B4513]" />}
              </button>
            ))}
          </div>
        </div>
      , document.body)}

      {/* Print choice modal */}
      {choiceModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setChoiceModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-xs p-4" onClick={(e) => e.stopPropagation()} data-testid="print-choice-modal">
            <div className="font-bold text-[#8B4513] mb-3">Print Report</div>
            <button onClick={() => { setChoiceModal(false); handlePrint(true); }} data-testid="print-limited"
              className="w-full text-left px-3 py-3 rounded-lg bg-[#f0f8ff] mb-1 text-sm font-semibold text-[#333]">Limited View</button>
            <p className="text-[11px] text-[#999] px-1 mb-2">Only Dough, Items, Today, Tmrw columns</p>
            <button onClick={() => { setChoiceModal(false); handlePrint(false); }} data-testid="print-full"
              className="w-full text-left px-3 py-3 rounded-lg bg-[#f0fff0] mb-1 text-sm font-semibold text-[#333]">Full View</button>
            <p className="text-[11px] text-[#999] px-1">All columns including Prepared & Not Done</p>
          </div>
        </div>
      , document.body)}

      {/* Excess stock alert modal */}
      {excessModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setExcessModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[75vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="excess-modal">
            <div className="flex items-center justify-between p-4 border-b border-[#eee]">
              <div className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-[#e65100]" /><span className="font-bold text-[#333]">Excess Stock Alert</span></div>
              <button onClick={() => setExcessModal(false)}><X className="w-6 h-6 text-[#666]" /></button>
            </div>
            <div className="p-4 overflow-y-auto">
              <p className="text-xs text-[#777] mb-2">Need = Today's Orders − Stock | Excess = Stock + Prepared − Today's Orders</p>
              {excessItems.length === 0 ? (
                <p className="text-center text-[#999] py-6">No excess stock found</p>
              ) : (
                <div className="border border-[#ddd] rounded-lg overflow-hidden text-xs">
                  <div className="flex bg-[#8B4513] text-white font-bold">
                    <div className="flex-[3] p-2">Item</div><div className="flex-1 p-2 text-center">Stock</div><div className="flex-1 p-2 text-center">Orders</div><div className="flex-1 p-2 text-center">Need</div><div className="flex-1 p-2 text-center">Excess</div>
                  </div>
                  {excessItems.map((it, i) => (
                    <div key={i} className={`flex border-t border-[#eee] ${i % 2 ? "bg-[#fafafa]" : ""}`} data-testid={`excess-item-${i}`}>
                      <div className="flex-[3] p-2 text-[#333]">{it.name}</div>
                      <div className="flex-1 p-2 text-center">{it.stock}</div>
                      <div className="flex-1 p-2 text-center">{it.todayOrders}</div>
                      <div className={`flex-1 p-2 text-center ${it.needToday < 0 ? "text-[#f44336]" : ""}`}>{it.needToday}</div>
                      <div className="flex-1 p-2 text-center font-bold text-[#f44336]">{it.excess}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-[#f0f0f0]">
              <button onClick={() => setExcessModal(false)} data-testid="excess-got-it"
                className="w-full py-3 rounded-xl bg-[#8B4513] hover:bg-[#703710] text-white font-bold">Got it</button>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
}
